import React, { useState, useEffect } from 'react';
import './ProductGallery.css';

interface ProductImage {
  id: string;
  src: string;
  alt: string;
  title: string;
  category: string;
}

interface ProductGalleryProps {
  images: ProductImage[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
}

export const ProductGallery: React.FC<ProductGalleryProps> = ({
  images,
  isOpen,
  onClose,
  initialIndex = 0
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div 
      className="product-gallery-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="dialog"
      aria-modal="true"
      aria-label="Ürün Galerisi"
    >
      <div className="product-gallery-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="product-gallery-header">
          <div className="product-gallery-info">
            <h3 className="product-gallery-title">{currentImage.title}</h3>
            <span className="product-gallery-category">{currentImage.category}</span>
          </div>
          <button 
            className="product-gallery-close"
            onClick={onClose}
            aria-label="Galeriyi Kapat"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Main Image */}
        <div className="product-gallery-main">
          {isLoading && (
            <div className="product-gallery-loading">
              <div className="loading-spinner"></div>
              <span>Yükleniyor...</span>
            </div>
          )}
          
          <img
            src={currentImage.src}
            alt={currentImage.alt}
            className="product-gallery-image"
            onLoad={handleImageLoad}
            onError={handleImageError}
            decoding="async"
            style={{ display: isLoading ? 'none' : 'block' }}
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                className="product-gallery-nav product-gallery-nav--prev"
                onClick={handlePrevious}
                aria-label="Önceki Görsel"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="product-gallery-nav product-gallery-nav--next"
                onClick={handleNext}
                aria-label="Sonraki Görsel"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="product-gallery-thumbnails">
            <div className="product-gallery-thumbnails-container">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  className={`product-gallery-thumbnail ${
                    index === currentIndex ? 'product-gallery-thumbnail--active' : ''
                  }`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`${image.title} görselini göster`}
                >
                  <img src={image.src} alt={image.alt} loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Counter */}
        {images.length > 1 && (
          <div className="product-gallery-counter">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductGallery;
